/**
 * Pool-agnostic typed API client factory.
 *
 * Lifted from `admin-app/src/lib/apiClient.ts` in Phase 9b-1 and generalised so each app
 * (admin, customer, and later candidate) calls `createApiClient(config)` with its own
 * cookie / refresh-endpoint / excluded-paths config. The transport behaviour is identical
 * across pools — only the names differ.
 *
 * Conventions:
 *   - Cross-origin fetch with `credentials: 'include'` so the pool's cookies travel with
 *     every request.
 *   - Mutations echo the pool's CSRF cookie value into the `x-qh-csrf` header
 *     (double-submit cookie pattern; backend issues + verifies it). The header name is
 *     constant across pools — only the cookie name differs.
 *   - Backend wraps success in `{ data: ... }` and errors in
 *     `{ error: { code, message, details, correlation_id } }` — both forms are unwrapped
 *     here so callers see clean payloads / typed errors.
 *   - 401 handling: the client attempts a single silent refresh on first 401 and retries
 *     the original request once. Concurrent 401s coalesce on a single in-flight refresh
 *     promise so a hundred parallel requests don't fire a hundred refreshes. Paths in
 *     `excludedRefreshPaths` skip retry (else infinite loop on the refresh endpoint
 *     itself, or the wrong UX on signin).
 *
 * Retry policy on rate limiting / 5xx: NONE — small, authenticated audience; retrying just
 * digs the hole deeper. Callers get an `ApiError` / `RateLimitError` and surface inline.
 */

export interface ApiClientConfig {
  /** Backend base URL, e.g. http://localhost:3100/api/v1 (no trailing slash). */
  baseUrl: string;
  /** CSRF cookie name for this pool (e.g. 'qh_admin_csrf', 'qh_company_csrf'). */
  csrfCookieName: string;
  /** Refresh endpoint path (e.g. '/admin/auth/refresh', '/company/auth/refresh'). */
  refreshEndpoint: string;
  /**
   * Paths whose 401 must NOT trigger a refresh-then-retry. The refresh endpoint itself
   * always belongs here (else infinite loop), as do the unauthenticated auth paths
   * (signin, setup, forgot-password, reset-password).
   */
  excludedRefreshPaths: string[];
}

const CSRF_HEADER = 'x-qh-csrf';

export interface ApiErrorDetail {
  field?: string;
  issue?: string;
  [k: string]: unknown;
}

export interface ApiErrorPayload {
  code: string;
  message: string;
  correlation_id?: string;
  details?: ApiErrorDetail[];
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly correlationId: string | undefined;
  readonly details: ApiErrorDetail[] | undefined;

  constructor(payload: ApiErrorPayload, status: number) {
    super(payload.message);
    this.name = 'ApiError';
    this.code = payload.code;
    this.status = status;
    this.correlationId = payload.correlation_id;
    this.details = payload.details;
  }
}

export class RateLimitError extends ApiError {
  readonly retryAfterSeconds: number;

  constructor(payload: ApiErrorPayload, status: number, retryAfterSeconds: number) {
    super(payload, status);
    this.name = 'RateLimitError';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

function parseRetryAfter(res: Response, error: ApiErrorPayload | null): number {
  if (error?.details && error.details.length > 0) {
    const v = (error.details[0] as { retry_after_seconds?: unknown }).retry_after_seconds;
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) return Math.max(1, Math.ceil(v));
  }
  const header = res.headers.get('retry-after');
  if (header) {
    const n = Number(header);
    if (Number.isFinite(n) && n > 0) return Math.max(1, Math.ceil(n));
  }
  return 60;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const all = document.cookie ? document.cookie.split('; ') : [];
  for (const entry of all) {
    const eq = entry.indexOf('=');
    if (eq < 0) continue;
    if (entry.slice(0, eq) === name) {
      return decodeURIComponent(entry.slice(eq + 1));
    }
  }
  return null;
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Skip the `{ data: ... }` envelope unwrap. */
  raw?: boolean;
}

export interface ApiClient {
  get: <T>(path: string, opts?: RequestOptions) => Promise<T>;
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => Promise<T>;
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => Promise<T>;
  delete: <T>(path: string, body?: unknown, opts?: RequestOptions) => Promise<T>;
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  const baseUrl = config.baseUrl.replace(/\/+$/, '');

  /**
   * Coalesces concurrent 401s onto a single refresh attempt so a page-load that fires 5
   * parallel queries doesn't fire 5 refreshes. Resolves to true if the refresh succeeded.
   * Cleared after each attempt so subsequent 401s — if the access token expires again
   * later in the session — re-attempt cleanly.
   */
  let inFlightRefresh: Promise<boolean> | null = null;

  async function attemptRefresh(): Promise<boolean> {
    if (inFlightRefresh) return inFlightRefresh;
    inFlightRefresh = (async () => {
      try {
        const headers: Record<string, string> = { Accept: 'application/json' };
        const csrf = readCookie(config.csrfCookieName);
        if (csrf) headers[CSRF_HEADER] = csrf;
        const res = await fetch(`${baseUrl}${config.refreshEndpoint}`, {
          method: 'POST',
          credentials: 'include',
          headers,
        });
        return res.ok;
      } catch {
        return false;
      }
    })();
    try {
      return await inFlightRefresh;
    } finally {
      inFlightRefresh = null;
    }
  }

  function shouldAttemptRefreshFor(path: string): boolean {
    for (const excluded of config.excludedRefreshPaths) {
      if (path.startsWith(excluded)) return false;
    }
    return true;
  }

  async function request<T>(
    method: Method,
    path: string,
    body?: unknown,
    opts: RequestOptions = {},
    isRetry = false,
  ): Promise<T> {
    const url = new URL(`${baseUrl}${path.startsWith('/') ? path : `/${path}`}`);
    if (opts.query) {
      for (const [key, value] of Object.entries(opts.query)) {
        if (value === undefined || value === null || value === '') continue;
        url.searchParams.set(key, String(value));
      }
    }

    const headers: Record<string, string> = { Accept: 'application/json' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';

    if (method !== 'GET') {
      const csrf = readCookie(config.csrfCookieName);
      if (csrf) headers[CSRF_HEADER] = csrf;
    }

    let res: Response;
    try {
      res = await fetch(url.toString(), {
        method,
        credentials: 'include',
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch {
      throw new ApiError(
        {
          code: 'NETWORK_ERROR',
          message: 'Could not reach the QuantumHire server. Check your connection and try again.',
        },
        0,
      );
    }

    if (res.status === 204) return undefined as T;

    const contentType = res.headers.get('content-type') ?? '';
    const isJson = contentType.includes('application/json');
    const payload: unknown = isJson ? await res.json().catch(() => null) : null;

    if (!res.ok) {
      if (res.status === 401 && !isRetry && shouldAttemptRefreshFor(path)) {
        const refreshed = await attemptRefresh();
        if (refreshed) {
          return request<T>(method, path, body, opts, true);
        }
      }
      const envelope = payload as { error?: ApiErrorPayload } | null;
      if (envelope && envelope.error) {
        if (envelope.error.code === 'RATE_LIMITED') {
          throw new RateLimitError(envelope.error, res.status, parseRetryAfter(res, envelope.error));
        }
        throw new ApiError(envelope.error, res.status);
      }
      if (res.status === 429) {
        throw new RateLimitError(
          { code: 'RATE_LIMITED', message: 'Too many requests. Please slow down and try again shortly.' },
          res.status,
          parseRetryAfter(res, null),
        );
      }
      throw new ApiError(
        { code: 'INTERNAL_ERROR', message: 'Something went wrong. Please try again.' },
        res.status,
      );
    }

    if (opts.raw) return payload as T;
    const dataEnv = payload as { data?: T } | null;
    return (dataEnv && 'data' in dataEnv ? (dataEnv.data as T) : (payload as T));
  }

  return {
    get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),
    post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('POST', path, body, opts),
    patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PATCH', path, body, opts),
    delete: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('DELETE', path, body, opts),
  };
}
